import Overlay from 'ol/Overlay';
import Polygon from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';
import Event from 'ol/events/Event';
import DragZoom from 'ol/interaction/DragZoom';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import OLDraw from 'ol/interaction/Draw';
import { unByKey } from 'ol/Observable';
import h from 'hyperscript';
import Feature from 'ol/Feature';
import { Circle, MultiLineString, MultiPolygon } from 'ol/geom';

import {
  getWebWorkerInstance
} from 'cadenza/map/interactions/classic/classic-geometry-edit-strategy/classic-union-without-intersection-worker-api';
import { animateDuringMapMove } from 'cadenza/map/map-utils';
import { SketchTextInput } from 'cadenza/sketch-text-input/sketch-text-input';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import { rectangleGeometryFunction } from 'cadenza/map/geom/rectangle-utilities';
import { Oval } from 'cadenza/map/geom/oval';
import { ovalGeometryFunction } from 'cadenza/map/geom/oval-utilties';
import { Line, lineGeometryFunction } from 'cadenza/map/geom/line';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { logger } from 'cadenza/utils/logging';

import i18n from './classic-draw-interaction.properties';

export const FEATURE_IN_PROGRESS_POTENTIALLY_DONE = 'featureInProgressPotentiallyDone';
export const IS_FEATURE_WITH_HOLES = 'hasHoles';
export const IS_FEATURE_VALID = 'isValid';

// Draw interaction extends OpenLayers Draw interaction with the following features:
// - help tooltip,
// - aborts drawing on Esc,
// - 'drawabort' event,
// - 'Oval', 'Rectangle' and 'Line' geometry types.
export class ClassicDrawInteraction extends OLDraw {

  /**
   * Creates a DrawInteraction instance
   *
   * @param { object } map - The OL map
   * @param {object} options - the options
   * @param {UnionWithoutIntersectionStrategy} [options.drawEndStrategy] - The drawEndStrategy
   * @param {boolean} [options.isMultiGeometry] - Whether we can draw Multi geometries
   * @param {GeometryType} [options.type] - What kind of geometry are we drawing
   * @param {VectorLayerSource} [options.source] - The source to which we will add the drawn feature
   * @param {StyleLike} [options.style] - The style for features while they are being drawn
   * @param {StyleLike} [options.featureStyle] - This style is used to style text input fields when drawing features with text
   * @param {boolean} [options.isTextModeDrawing] - Whether we're creating a text sketch item
   */
  constructor (map, { drawEndStrategy, isMultiGeometry, ...options }) {
    if (options.type === GeometryType.OVAL) {
      options.type = GeometryType.LINE_STRING;
      options.geometryFunction = (coords, geom) => ovalGeometryFunction(coords, geom, this._shiftPressed());
      options.maxPoints = 2;
    } else if (options.type === GeometryType.RECTANGLE) {
      options.type = GeometryType.LINE_STRING;
      options.geometryFunction = (coords, geom) => rectangleGeometryFunction(coords, geom, this._shiftPressed());
      options.maxPoints = 2;
    } else if (options.type === GeometryType.LINE) {
      options.type = GeometryType.LINE_STRING;
      options.geometryFunction = lineGeometryFunction;
      options.maxPoints = 2;
    }

    options.condition = () => true; // by default draw ignores events when any modifier key is pressed, don't ignore events
    options.freehandCondition = () => false; // by default draw goes into freehand if shift is pressed, don't go into freehand

    super(options);
    this.setActive(false);

    this._map = map;
    this._helpTooltip = null;
    this._isTextModeDrawing = options.isTextModeDrawing;
    this._drawEndStrategy = drawEndStrategy;
    this._isMultiGeometry = isMultiGeometry;
    this._source = options.source;
    if (this._isTextModeDrawing) {
      this._sketchTextInput = this._createSketchTextInput(options.featureStyle);
    }

    this._baseHandlers = {
      drawstart: event => {
        this._sketch = event.feature;
        hideHelpTooltip(this._helpTooltip);
        this._deactivateZoomInteractions();
      },
      drawend: () => {
        // 'drawend' event is dispatched from olDraw.js::finishDrawing, if you modify this._sketch here
        // it will modify the this.sketchFeature_ there
        const sketchGeometry = this._sketch.getGeometry();
        if (sketchGeometry instanceof Rectangle) {
          sketchGeometry.normalize();
        }

        this._sketch = null;
        hideHelpTooltip(this._helpTooltip);
        // CADENZA-8607
        // Add restoring the interaction state to the end of the event loop,
        // so that the finish event is not handled by zoom interactions.
        // (The drawend event does not support stopImmediatePropagation().)
        setTimeout(() => this._restoreZoomInteractionState());
      },
      drawabort: () => {
        this._sketch = null;
        hideHelpTooltip(this._helpTooltip);
        this._restoreZoomInteractionState();
      },
      pointerdown: () => {
        if (this._isTextModeDrawing && this._sketch) {
          this._cancelDrawing();
        }
      },
      pointermove: event => {
        this._lastPointerMoveEvent = event.originalEvent;
        const helpMessage = this.getHelpMessage(this._sketch ? this._sketch.getGeometry() : null, event, this._isTextModeDrawing);
        updateHelpTooltip(this._helpTooltip, event, { helpMessage });
      },
      mouseout: () => { hideHelpTooltip(this._helpTooltip); },
      keydown: event => { this._cancelOnEscape(event); },
      mapmove: () => { this._repositionSketchTextInput(); }
    };

    this.on('change:active', () => {
      if (this.getActive()) {
        this._activateHelpTooltip();
      } else {
        this._deactivateHelpTooltip();
      }
    });
  }

  _deactivateZoomInteractions () {
    this._dragZoomPreviousState = this._setInteractionActive(DragZoom, false);
    this._doubleClickZoomPreviousState = this._setInteractionActive(DoubleClickZoom, false);
  }

  _restoreZoomInteractionState () {
    this._setInteractionActive(DragZoom, this._dragZoomPreviousState);
    this._setInteractionActive(DoubleClickZoom, this._doubleClickZoomPreviousState);
  }

  _setInteractionActive (interactionClass, active) {
    const interactions = this._map.getInteractions().getArray();
    const interaction = interactions.find(interaction => interaction instanceof interactionClass);
    let previousActiveState = false;
    if (interaction) {
      previousActiveState = interaction.getActive();
      interaction.setActive(active);
    }
    return previousActiveState;
  }

  _activateHelpTooltip () {
    this._helpTooltip = addHelpTooltipToMap(this._map);

    this._eventKeys = [
      this.on('drawstart', this._baseHandlers.drawstart),
      this.on('drawend', this._baseHandlers.drawend),
      this.on('drawabort', this._baseHandlers.drawabort),
      this._map.on('pointermove', this._baseHandlers.pointermove),
      this._map.on('pointerdown', this._baseHandlers.pointerdown),
      ...animateDuringMapMove(this._map, this._baseHandlers.mapmove)
    ];

    document.addEventListener('keydown', this._baseHandlers.keydown);
    this._map.getViewport().addEventListener('mouseout', this._baseHandlers.mouseout);
  }

  _deactivateHelpTooltip () {
    removeHelpTooltipFromMap(this._helpTooltip, this._map);
    this._helpTooltip = null;

    unByKey(this._eventKeys);

    document.removeEventListener('keydown', this._baseHandlers.keydown);
    this._map.getViewport().removeEventListener('mouseout', this._baseHandlers.mouseout);
  }

  /**
   * Provides i18n messages based on the geometry being drawn and the phase in which we're in
   *
   * @protected
   * @param geometry - the geometry being drawn
   * @param event - provides info regarding the phase we're in
   * @param isTextModeDrawing - whether we're drawing text
   * @return the i18n message
   */
  getHelpMessage (geometry, event, isTextModeDrawing) {
    if (geometry) {
      if (geometry instanceof Oval || geometry instanceof Rectangle || geometry instanceof Circle) {
        return i18n('classic.draw.finishDrawing');
      } else if (geometry instanceof Polygon) {
        if (this.atFinish_(event.pixel)) {
          return i18n('classic.draw.finishDrawing');
        } else {
          return i18n('classic.draw.continuePolygonDrawing');
        }
      } else if (geometry instanceof Line) {
        return i18n('classic.draw.finishLineDrawing');
      } else if (geometry instanceof LineString) {
        return i18n('classic.draw.continueLineDrawing');
      } else if (isTextModeDrawing) {
        return i18n('classic.draw.finishText');
      }
    } else {
      return i18n('classic.draw.startDrawing');
    }
  }

  _cancelOnEscape (event) {
    if ((event.key === 'Escape' || event.key === 'Esc' /* IE11 */) && this._sketch) {
      this._cancelDrawing();
    }
  }

  _cancelDrawing () {
    this.abortDrawing_();
    if (this._sketchTextInput) {
      this._sketchTextInput.hide();
    }
    this.dispatchEvent(new Event('drawabort'));
  }

  /* The draw end event and geometry function arguments do not contain the original mouse event which has the shift and ctrl modifiers,
   * therefore we need to cache the event here in order to know if the user has the shift and ctrl pressed */
  getLastPointerMoveEvent () {
    return this._lastPointerMoveEvent;
  }

  _shiftPressed () {
    return this._lastPointerMoveEvent && this._lastPointerMoveEvent.shiftKey;
  }

  // @Overrides ol.interaction.Draw.finishDrawing
  finishDrawing () {
    if (this._isTextModeDrawing && !this._sketchTextInput.isShown()) {
      this._startTextInput();
    } else {
      super.finishDrawing();
    }
  }

  // Called from ol draw.js through a patch.
  // We do it in this way so that we don't dispatch events from super.finishDrawing (drawend, addfeature) with incorrect/incomplete geometries
  async _overrideSketchFeatureCreator (sketchFeature) {
    if (this._drawEndStrategy && this._drawEndStrategy.isApplicable(sketchFeature.getGeometry())) {
      return await applyDrawEndStrategy(sketchFeature, this._source, this._isMultiGeometry, this._map.getTargetElement());
    }
    return sketchFeature;
  }

  _startTextInput () {
    const coordinate = this.sketchFeature_.getGeometry().getFirstCoordinate();
    const position = this._map.getPixelFromCoordinate(coordinate);
    this._sketchTextInput.show(position);

    this.sketchPoint_ = null;
    this.overlay_.getSource().clear(true);
  }

  /**
   * Overrides OL5 method
   *
   * @param event - event
   * @private
   */
  modifyDrawing_ (event) {
    // don't modify text point coordinates on mouse move, if text input is already shown
    if (this._isTextModeDrawing && this._sketchTextInput.isShown()) {
      return;
    }

    super.modifyDrawing_(event);
  }

  _createSketchTextInput (featureStyle) {
    const sketchTextInput = new SketchTextInput(featureStyle);
    sketchTextInput.addEventListener('cancel', () => this._cancelDrawing());
    sketchTextInput.addEventListener('finish', event => this.finishTextDraw(event.detail.text.trim()));
    this._map.getTargetElement().append(sketchTextInput);
    return sketchTextInput;
  }

  _repositionSketchTextInput () {
    if (this.sketchFeature_ && this._isTextModeDrawing && this._sketchTextInput.isShown()) {
      const coordinate = this.sketchFeature_.getGeometry().getFirstCoordinate();
      this._sketchTextInput.position = this._map.getPixelFromCoordinate(coordinate);
    }
  }

  /**
   * Handler executed after the user finished entering sketch text item content
   *
   * @param text - text content from the input
   * @protected
   */
  finishTextDraw (text) {
    if (this.sketchFeature_) {
      this.sketchFeature_.getStyle().getText().setText(text);
      this.sketchFeature_.set(SketchFeatureProperty.STYLE_ITEM, { lines: text.split('\n') });
      this.finishDrawing();
    }
  }

  dispatchEventIfFeatureIsPotentiallyDone () {
    const type = this.type_;
    const sketchFeature = this.sketchFeature_;
    if (isFeatureInProgressPotentiallyDone(sketchFeature, this.mode_, this.sketchCoords_, this.minPoints_)) {
      let feature = sketchFeature;
      // For multi geometry we need to convert the simple geometries to multi geometries,
      // to make it coherent with the geometry type which features from source.getFeatures() have
      if (type === GeometryType.MULTI_LINE_STRING) {
        feature = new Feature(new MultiLineString([ sketchFeature.getGeometry().getCoordinates() ]));
      } else if (type === GeometryType.MULTI_POLYGON) {
        feature = new Feature(new MultiPolygon([ sketchFeature.getGeometry().getCoordinates() ]));
      }
      this.dispatchEvent(new FeatureInProgressPotentiallyDoneEvent(feature));
    }
  }

}

export function addHelpTooltipToMap (map, { helpMessage } = {}) {
  const helpTooltip = new Overlay({
    element: h('.tooltip', { hidden: true }),
    offset: [ 15, 0 ],
    positioning: 'center-left'
  });
  if (helpMessage) {
    helpTooltip.getElement().textContent = helpMessage;
  }
  map.addOverlay(helpTooltip);
  return helpTooltip;
}

export function updateHelpTooltip (helpTooltip, event = {}, { helpMessage } = {}) {
  if (!event.dragging && helpTooltip) {
    if (helpMessage) {
      helpTooltip.getElement().textContent = helpMessage;
    }
    if (event.coordinate) {
      helpTooltip.setPosition(event.coordinate);
    }

    helpTooltip.getElement().hidden = false;
  }
}

export function removeHelpTooltipFromMap (helpTooltip, map) {
  helpTooltip.getElement().remove();
  map.removeOverlay(helpTooltip);
}

export function hideHelpTooltip (helpTooltip) {
  if (helpTooltip) {
    helpTooltip.getElement().hidden = true;
  }
}

function isFeatureInProgressPotentiallyDone (sketchFeature, mode, sketchCoords, minPoints) {
  // We only care about geometries which require more than 2 mouseUp events to be drawn, the others are considered not in progress or done
  // Inspired by Draw.js::atFinish_
  if (!sketchFeature || !sketchCoords) {
    return false;
  }
  let potentiallyDone = false;
  if (mode === GeometryType.LINE_STRING) {
    potentiallyDone = sketchCoords.length > minPoints;
  } else if (mode === GeometryType.POLYGON) {
    potentiallyDone = sketchCoords[0].length > minPoints;
  }
  return potentiallyDone;
}

class FeatureInProgressPotentiallyDoneEvent extends Event {

  _feature;

  constructor (feature) {
    super(FEATURE_IN_PROGRESS_POTENTIALLY_DONE);
    this._feature = feature;
  }

  get feature () {
    return this._feature;
  }

}

async function applyDrawEndStrategy (featureUnderEdit, source, isMultiPolygonMode, mapTargetElement) {
  // Let OL add the new feature to the map
  // Start the worker, when the worker is done, reset the geometry on the featureUnderEdit
  // Look into not removing the original geometry until the operation is done, it may look weird otherwise,
  // especially when the geometry is not valid
  const currentGeometries = source.getFeatures().map(f => f.getGeometry());
  const newGeometry = featureUnderEdit.getGeometry();
  return await getWebWorkerInstance(mapTargetElement).mergeGeometries(currentGeometries, newGeometry)
    .then(mergedGeometry => {
      if (mergedGeometry) {
        featureUnderEdit.set(IS_FEATURE_WITH_HOLES, true);
        featureUnderEdit.set(IS_FEATURE_VALID, isMultiPolygonMode || mergedGeometry.getPolygons().length === 1);
        const validGeometry = getValidGeometry(currentGeometries, mergedGeometry, isMultiPolygonMode);
        featureUnderEdit.setGeometry(validGeometry);
      }
      return featureUnderEdit;
    }).catch(error => {
      logger.error('Error while drawing', error);
      featureUnderEdit.set(IS_FEATURE_VALID, false); // So that it's not added to the map
      return featureUnderEdit;
    });
}

// If we're drawing a polygon, the mergedGeometry can only contain 1 geometry, in other words:
// If you're trying to edit a polygon, you cannot draw more than 1 polygon.
function getValidGeometry (geometriesBeforeDrawing, mergedGeometry, isMultiPolygonEditMode) {
  if (isMultiPolygonEditMode) {
    return mergedGeometry;
  }
  const numberOfPolygonsInMultiPolygon = mergedGeometry.getPolygons().length;
  if (numberOfPolygonsInMultiPolygon !== 1) {
    return geometriesBeforeDrawing[0]; // Not valid, so restore previous state
  } else {
    return mergedGeometry.getPolygons()[0];
  }
}
